import type { ReactElement } from 'react';

import { RESOLVER_CONTENT_CLASSNAME } from '@e-socna/printer-contract';
import type { ExternalReferenceTypeId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useMissionContextOptional } from '../../../contexts/MissionContext/hooks/useMissionContextOptional.hook';

import type {
  ResolverParameters,
  ResolverResult,
} from '../types/resolvers.type';

import { useMissionQuickAccess } from './hooks/useMissionQuickAccess.hook';
import { externalReferencesLinesOf } from './utils/external-references-lines.util';

const REFERENCES_COLOR = '#e00000';

// The object of the quotation, then the references the external actors give the mission, one
// line per actor, under its text.
export const useDevisObjet = (
  parameters: Readonly<ResolverParameters>,
): ResolverResult<ReactElement> => {
  const { actorCategoriesCodeMap, externalReferenceTypesIdMap } =
    useReferencesContext();
  // Out of a mission (a template previewed), the object goes without references.
  const missionContext = useMissionContextOptional();
  const quickAccess = useMissionQuickAccess({
    ...parameters,
    enabled: parameters.enabled && !!missionContext,
  });

  if (!parameters.enabled) {
    return {
      data: undefined,
      error: null,
      isSuccess: false,
      isLoading: false,
    };
  }

  if (missionContext && !quickAccess.isSuccess) {
    return {
      data: undefined,
      error: quickAccess.error,
      isSuccess: false,
      isLoading: quickAccess.isLoading,
    };
  }

  const referencesLines = quickAccess.data
    ? externalReferencesLinesOf({
        actors: quickAccess.data.consolidation.externalActors.map((actor) => ({
          categoryIds: actor.categoryIds,
          thirdParty: actor.thirdParty,
          references: 'references' in actor ? actor.references : [],
        })),
        quotationReceiverCategoryId:
          actorCategoriesCodeMap.QuotationReceiver._id,
        documentLabelOf: (typeId) =>
          externalReferenceTypesIdMap[typeId as ExternalReferenceTypeId]
            ?.documentLabel,
      })
    : [];

  return {
    data: {
      generated: (
        <div className={RESOLVER_CONTENT_CLASSNAME}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: '0.3em',
            }}
          >
            <strong>Objet :</strong>
            <strong>Proposition d&apos;honoraires</strong>
            {referencesLines.map((line) => (
              <strong
                key={line}
                style={{ gridColumn: 2, color: REFERENCES_COLOR }}
              >
                {line}
              </strong>
            ))}
          </div>
        </div>
      ),
    },
    error: null,
    isSuccess: true,
    isLoading: false,
  };
};
